import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Package, PackagePlus } from "lucide-react";
import { api, formatApiError } from "../api";
import { RETAIL } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { formatCurrency } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { StatusPill } from "../components/common/StatusPill";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { FormField, isFieldValid } from "../components/common/FormField";
import { SubmitButton } from "../components/common/SubmitButton";

const TABS = [{ key: "products", label: "Products" }, { key: "stock", label: "Stock" }, { key: "revenue", label: "Revenue" }];
const CATEGORIES = [{ value: "supplement", label: "Supplement" }, { value: "merch", label: "Merch" }, { value: "consumable", label: "Consumable" }];

// UX-GAP-01 §1.10: Owner Cockpit "Retail" (module-gated, see App.js's `ModuleGate` wrapper) —
// was a StubPage. 3 tabs against the pre-existing `/products`, `/stock`, `/reports/retail-revenue`
// endpoints. `is_low_stock` is computed server-side (`retail_engine.py`) — the badge here just
// surfaces that real flag, never a client-side guess.
export default function Retail() {
  const { branches, selectedBranchId } = useAppData();
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("products");
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [branchId, setBranchId] = useState(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id || "");

  useEffect(() => { if (!branchId && branches.length) setBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0].id); }, [branches, selectedBranchId, branchId]);

  return (
    <div className="space-y-5 max-w-4xl" data-testid={RETAIL.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Retail</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>Products, stock levels, and how much retail actually earns.</p>
        </div>
        {tab === "products" && (
          <Button data-testid={RETAIL.addProductButton} onClick={() => setAddProductOpen(true)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
            <Package size={15} /> Add product
          </Button>
        )}
      </motion.div>

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={RETAIL.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && <ProductsTab />}
      {tab === "stock" && <StockTab branchId={branchId} setBranchId={setBranchId} branches={branches} />}
      {tab === "revenue" && <RevenueTab branchId={branchId} setBranchId={setBranchId} branches={branches} />}

      <AddProductDialog open={addProductOpen} onOpenChange={setAddProductOpen} />
    </div>
  );
}

function ProductsTab() {
  const productsQ = useQuery({ queryKey: ["retail-products"], queryFn: async () => (await api.get("/products")).data.products });

  if (productsQ.isError) return <ErrorState error={productsQ.error} onRetry={() => productsQ.refetch()} />;
  if (productsQ.isLoading) return <GymLoader label="Loading products…" />;
  if (productsQ.data.length === 0) return <div className="solid-surface rounded-[14px] p-10 text-center text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No products yet.</div>;

  return (
    <div className="solid-surface rounded-[14px] overflow-hidden">
      <table className="w-full text-sm" data-testid={RETAIL.productTable}>
        <thead>
          <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
            <th className="eyebrow px-5 py-3">Product</th>
            <th className="eyebrow px-5 py-3">Category</th>
            <th className="eyebrow px-5 py-3">Price</th>
            <th className="eyebrow px-5 py-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {productsQ.data.map((p) => (
            <tr key={p.id} data-testid={RETAIL.productRow(p.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
              <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{p.name}</td>
              <td className="px-5 py-3.5 capitalize" style={{ color: "var(--scarlet-text-secondary)" }}>{p.category}</td>
              <td className="px-5 py-3.5 tabular-nums-force font-medium" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(p.price)}</td>
              <td className="px-5 py-3.5"><StatusPill status="active" label={p.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddProductDialog({ open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ name: "", category: "supplement", price: "", hsn: "" });
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setForm({ name: "", category: "supplement", price: "", hsn: "" }); setError(""); } }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post("/products", { name: form.name, category: form.category, price: Math.round(Number(form.price) * 100), hsn_sac: form.hsn }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["retail-products"] }); onOpenChange(false); },
    onError: (e) => setError(formatApiError(e).message),
  });

  const isValid = isFieldValid("text", true, form.name) && isFieldValid("number", true, form.price) && form.hsn.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={RETAIL.productFormDialog}>
        <DialogHeader>
          <DialogTitle>Add product</DialogTitle>
          <DialogDescription>Restock it from the Stock tab once it's saved.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={RETAIL.productFormError}>{error}</p>}
        <div className="space-y-3">
          <FormField label="Product name" required testId={RETAIL.productNameInput} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select onValueChange={(v) => setForm({ ...form, category: v })} value={form.category}>
              <SelectTrigger data-testid={RETAIL.productCategorySelect}><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <FormField label="Price (₹)" required type="number" testId={RETAIL.productPriceInput} value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
          <FormField label="HSN/SAC code" required testId={RETAIL.productHsnInput} value={form.hsn} onChange={(v) => setForm({ ...form, hsn: v })} helperText="Required for GST invoicing." />
        </div>
        <DialogFooter>
          <SubmitButton testId={RETAIL.productFormSubmit} disabled={!isValid} pending={mutation.isPending} onClick={() => { setError(""); mutation.mutate(); }} label="Add product" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BranchPicker({ testId, branchId, setBranchId, branches }) {
  return (
    <Select onValueChange={setBranchId} value={branchId || undefined}>
      <SelectTrigger data-testid={testId} className="w-56"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
      <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function StockTab({ branchId, setBranchId, branches }) {
  const [restockOpen, setRestockOpen] = useState(false);
  const productsQ = useQuery({ queryKey: ["retail-products"], queryFn: async () => (await api.get("/products")).data.products });
  const stockQ = useQuery({ queryKey: ["retail-stock", branchId], queryFn: async () => (await api.get(`/stock/${branchId}`)).data.stock, enabled: !!branchId });
  const productById = Object.fromEntries((productsQ.data || []).map((p) => [p.id, p]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <BranchPicker testId={RETAIL.stockBranchSelect} branchId={branchId} setBranchId={setBranchId} branches={branches} />
        <Button data-testid={RETAIL.restockButton} variant="outline" onClick={() => setRestockOpen(true)} className="gap-2" disabled={!branchId}><PackagePlus size={15} /> Restock</Button>
      </div>
      {stockQ.isLoading && <GymLoader label="Loading stock…" />}
      {stockQ.data && stockQ.data.length === 0 && <div className="solid-surface rounded-[14px] p-10 text-center text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No stock recorded for this branch yet.</div>}
      {stockQ.data && stockQ.data.length > 0 && (
        <div className="solid-surface rounded-[14px] overflow-hidden">
          <table className="w-full text-sm" data-testid={RETAIL.stockTable}>
            <thead>
              <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <th className="eyebrow px-5 py-3">Product</th>
                <th className="eyebrow px-5 py-3">Quantity</th>
                <th className="eyebrow px-5 py-3">Threshold</th>
                <th className="eyebrow px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {stockQ.data.map((s) => (
                <tr key={s.id} data-testid={RETAIL.stockRow(s.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                  <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{productById[s.product_ref]?.name || s.product_ref}</td>
                  <td className="px-5 py-3.5 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{s.quantity}</td>
                  <td className="px-5 py-3.5 tabular-nums-force" style={{ color: "var(--scarlet-text-secondary)" }}>{s.low_stock_threshold}</td>
                  <td className="px-5 py-3.5">{s.low_stock && <span data-testid={RETAIL.lowStockBadge(s.id)} className="flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--scarlet-neg)" }}><AlertTriangle size={12} /> Low stock</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RestockDialog open={restockOpen} onOpenChange={setRestockOpen} branchId={branchId} products={productsQ.data || []} />
    </div>
  );
}

function RestockDialog({ open, onOpenChange, branchId, products }) {
  const queryClient = useQueryClient();
  const [productRef, setProductRef] = useState("");
  const [quantity, setQuantity] = useState("");
  const [threshold, setThreshold] = useState("5");
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setProductRef(""); setQuantity(""); setThreshold("5"); setError(""); } }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post("/stock/restock", { branch_id: branchId, product_ref: productRef, quantity: Number(quantity), low_stock_threshold: Number(threshold) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["retail-stock", branchId] }); onOpenChange(false); },
    onError: (e) => setError(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={RETAIL.restockFormDialog}>
        <DialogHeader><DialogTitle>Restock</DialogTitle></DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }}>{error}</p>}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Product</Label>
            <Select onValueChange={setProductRef} value={productRef || undefined}>
              <SelectTrigger data-testid={RETAIL.restockProductSelect}><SelectValue placeholder="Pick a product" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <FormField label="Quantity to add" required type="number" testId={RETAIL.restockQuantityInput} value={quantity} onChange={setQuantity} />
          <FormField label="Low-stock threshold" required type="number" testId={RETAIL.restockThresholdInput} value={threshold} onChange={setThreshold} />
        </div>
        <DialogFooter>
          <SubmitButton testId={RETAIL.restockFormSubmit} disabled={!productRef || !quantity} pending={mutation.isPending} onClick={() => { setError(""); mutation.mutate(); }} label="Restock" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevenueTab({ branchId, setBranchId, branches }) {
  const revenueQ = useQuery({ queryKey: ["retail-revenue", branchId], queryFn: async () => (await api.get("/reports/retail-revenue", { params: { branch_id: branchId } })).data, enabled: !!branchId });

  return (
    <div className="space-y-4">
      <BranchPicker testId={RETAIL.revenueBranchSelect} branchId={branchId} setBranchId={setBranchId} branches={branches} />
      {revenueQ.isLoading && <GymLoader label="Loading revenue…" />}
      {revenueQ.data && (
        <div className="solid-surface rounded-[14px] p-5 space-y-3" data-testid={RETAIL.revenueByCategory}>
          <p className="eyebrow">Revenue by category</p>
          {Object.keys(revenueQ.data.by_category).length === 0 && <p className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No retail sales in this branch yet.</p>}
          {Object.entries(revenueQ.data.by_category).map(([cat, amt]) => (
            <div key={cat} className="flex items-center justify-between text-sm">
              <span className="capitalize" style={{ color: "var(--scarlet-text)" }}>{cat}</span>
              <span className="font-semibold tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{formatCurrency(amt)}</span>
            </div>
          ))}
          <p data-testid={RETAIL.marginNote} className="text-xs pt-2" style={{ color: "var(--scarlet-text-secondary)", borderTop: "1px solid var(--scarlet-hairline)" }}>{revenueQ.data.margin_note}</p>
        </div>
      )}
    </div>
  );
}
